import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICE_ICONS } from "./icons";
import { SERVICES } from "../../data/content";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };

const TechnicalBlueprint = () => (
  <svg
    aria-hidden="true"
    data-testid="services-blueprint"
    className="absolute inset-0 w-full h-full text-[#1E4E9C] opacity-[0.06] pointer-events-none"
    viewBox="0 0 1200 800"
    preserveAspectRatio="none"
  >
    <defs>
      <pattern id="services-blueprint-grid" width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="800" fill="url(#services-blueprint-grid)" />
    <g fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M110 620 H410 V500 H560 V620 H760 V430 H1040" />
      <path d="M180 180 H430 V300 H650 V180 H930" />
      <path d="M900 620 L1040 430 M760 620 L900 430" />
      <circle cx="410" cy="500" r="34" />
      <circle cx="930" cy="180" r="46" />
      <path d="M890 180 H970 M930 140 V220" />
      <path d="M220 650 V700 H520 M520 650 V700" />
    </g>
  </svg>
);

const SectionLine = ({ children, delay, className = "" }) => (
  <motion.span
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    className={`block overflow-hidden ${className}`}
  >
    <motion.span
      variants={{
        hidden: { y: "105%" },
        show: { y: 0, transition: { duration: 0.65, delay, ease: EASE } },
      }}
      className="block"
    >
      {children}
    </motion.span>
  </motion.span>
);

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.68, delay, ease: EASE },
  }),
  hover: { y: -4, transition: { duration: 0.25, ease: "easeOut" } },
};

const imageMaskVariants = {
  hidden: { clipPath: "inset(100% 0% 0% 0%)" },
  show: (delay) => ({
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.72, delay: delay + 0.08, ease: EASE },
  }),
};

const ServiceCard = ({ s, i }) => {
  const Icon = SERVICE_ICONS[s.icon];
  const [open, setOpen] = useState(false);
  const revealDelay = 0.34 + Math.floor(i / 3) * 0.14 + (i % 3) * 0.045;

  useEffect(() => {
    const handler = (e) => setOpen(e.detail === i);
    window.addEventListener("open-service", handler);
    return () => window.removeEventListener("open-service", handler);
  }, [i]);

  return (
    <motion.article
      id={`service-${i}`}
      data-testid={`service-card-${s.num}`}
      onClick={() => setOpen(!open)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen(!open);
        }
      }}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
      variants={cardVariants}
      custom={revealDelay}
      whileHover="hover"
      className={`group relative bg-white border border-[#E1E5E8] rounded-md hover:border-[#F47A20]/50 hover:shadow-sm transition-[border-color,box-shadow] duration-300 h-full flex flex-col overflow-hidden cursor-pointer ${i === 6 ? "lg:col-start-2" : ""}`}
    >
      <motion.div className="relative aspect-video overflow-hidden bg-[#EEF0EF]" variants={imageMaskVariants}>
        <motion.img
          src={s.image}
          alt={s.title}
          className="w-full h-full object-cover"
          variants={{ hover: { scale: 1.03 } }}
          transition={{ duration: 0.7, ease: EASE }}
        />
      </motion.div>

      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-center justify-between">
          <span className="font-manrope text-sm font-bold tracking-[0.18em] text-[#F47A20]">{s.num}</span>
          <span className="w-10 h-10 border border-[#F47A20]/25 text-[#F47A20] rounded-sm flex items-center justify-center">
            <Icon size={19} strokeWidth={1.6} />
          </span>
        </div>
        <h3 className="font-manrope font-bold tracking-tight text-[#17232D] text-[22px] leading-tight mt-5">
          {s.title}
        </h3>
        <p className="font-inter text-[#59636D] text-[15px] leading-[1.7] mt-3">
          {s.desc}
        </p>
        <div className="flex flex-wrap gap-2 mt-5">
          {s.tags.map((t) => (
            <span key={t} className="font-inter text-[10px] font-semibold uppercase tracking-[0.11em] text-[#59636D] bg-[#F7F7F4] border border-[#E1E5E8] rounded px-2.5 py-1.5">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-auto pt-6 flex items-center justify-between border-t border-[#EEF0F1]" data-testid={`service-toggle-${s.num}`}>
          <span className="font-manrope text-[11px] font-bold uppercase tracking-[0.18em] text-[#17232D]">
            {open ? "Close Service" : "Explore Service"}
          </span>
          <ArrowUpRight size={18} className="text-[#F47A20] transition-transform duration-300 group-hover:translate-x-1" />
        </div>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="pt-5 mt-5 border-t border-[#E1E5E8]" data-testid={`service-details-${s.num}`}>
                <ul className="grid grid-cols-1 gap-y-3">
                  {s.details.map((d) => (
                    <li key={d} className="font-inter text-[#17232D] text-[14px] leading-relaxed flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 bg-[#F47A20] mt-2 shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 bg-[#F7F7F4] border-y border-[#E1E5E8] relative overflow-hidden">
      <TechnicalBlueprint />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.55, ease: EASE }}
            className="font-manrope text-[11px] font-bold uppercase tracking-[0.3em] text-[#F47A20]"
            data-testid="services-section-label"
          >
            Our Capabilities
          </motion.p>
          <h2 className="font-manrope font-bold tracking-tight leading-[1.08] text-[42px] md:text-[48px] mt-5" data-testid="services-section-heading">
            <SectionLine delay={0.12} className="text-[#17232D]">
              INTEGRATED ENGINEERING.
            </SectionLine>
            <SectionLine delay={0.24} className="text-[#F47A20]">
              COMPLETE PROJECT CAPABILITY.
            </SectionLine>
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: 0.36, ease: EASE }}
            className="font-inter text-[#59636D] text-base md:text-[17px] leading-[1.65] mt-6 max-w-2xl"
            data-testid="services-caption"
          >
            From specialist engineering disciplines to project execution, our capabilities are structured to support complex industrial, pharmaceutical, laboratory and commercial projects.
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-card-grid">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.num} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
